import React, { createContext, useCallback, useContext, useState } from 'react';
import BuscaPlaca from '../components/BuscaPlaca';
import CSVResults from '../components/CSVResults';
import ListPlatesBD from '../components/ListPlatesBD';
import ListResults from '../components/ListResults';
import Loading from '../components/Loading';
import Operations from '../components/Operations';
import PlacaDetails from '../components/PlacaDetails';
import UploadFile from '../components/UploadFile';
import apiPlaca from '../services/apiPlaca';
import { warnToast } from '../utils/toast';

export interface Car {
  car_id: number;
  car_plate: string;
  car_chassi: string;
  car_current_contract: string;
  car_model: string;
  que_found: string;
  que_recuperator: string;
  que_recovery_place: string;
  que_comments: string;
  loc_name: string;
  loc_city: string;
  loc_state: string;
}

interface StepContextData {
  inputValue: string;
  currentPage: number;
  numberPerPage: string;
  operation: string;
  progress: number;
  processingPlates: number;
  listPlatesPerPage: Car[];
  diffPlates: Car[];
  car: Car;
  currentStep: number;
  changeStep: () => React.ReactElement;
  handleChangeCurrentStep: () => void;
  handleReturnCurrentStep: () => void;
  handleInitialCurrentStep: () => void;
  searchPlate: (plate_id: string) => void;
  getDiffPlates: () => void;
  getListPlatesPerPage: () => void;
  setOperation: (value: string) => void;
  setProgress: (value: number) => void;
  setProcessingPlates: (value: number) => void;
  setNumberPerPage: (value: string) => void;
  setInputValue: (value: string) => void;
  setCurrentPage: (value: number) => void;
}

const StepContext = createContext<StepContextData>({} as StepContextData);

const StepProvider: React.FC = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [car, setCar] = useState({} as Car);
  const [progress, setProgress] = useState(0);
  const [operation, setOperation] = useState('');
  const [processingPlates, setProcessingPlates] = useState(0);
  const [diffPlates, setDiffPlates] = useState([] as Car[]);
  const [listPlatesPerPage, setListPlatesPerPage] = useState([] as Car[]);
  const [numberPerPage, setNumberPerPage] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const handleChangeCurrentStep = useCallback(() => {
    setCurrentStep(currentStep + 1);
  }, [currentStep]);

  const handleReturnCurrentStep = useCallback(() => {
    setCurrentStep(currentStep - 1);
    setInputValue('');
  }, [currentStep]);

  const handleInitialCurrentStep = useCallback(() => {
    setCurrentStep(1);
  }, [currentStep]);

  const changeStep = useCallback(() => {
    switch (currentStep) {
      case 1:
        return <Operations />;
      case 2:
        if (operation === 'search') {
          return <BuscaPlaca />;
        }
        if (operation === 'listPlates') {
          return <ListPlatesBD />;
        }
        return <UploadFile />;
      case 3:
        if (operation === 'search') {
          return <PlacaDetails />;
        }
        if (operation === 'listPlates') {
          return <ListResults />;
        }
        return <CSVResults />;
      case 4:
        return <PlacaDetails />;
      default:
        return <></>;
    }
  }, [currentStep]);

  async function searchPlate(plate_id: string) {
    setLoading(true);
    setCar({} as Car);
    const { data } = await apiPlaca
      .get(`/SearchQuery/${plate_id}`)
      .then(response => response)
      .catch(error => error.response);

    if (data.message) {
      setLoading(false);
      warnToast('Nenhuma placa encontrada!');
      return;
    }

    setCar(data);
    setLoading(false);
    handleChangeCurrentStep();
  }

  async function getDiffPlates() {
    setLoading(true);
    const { data } = await apiPlaca
      .get('statusCars')
      .then(response => response)
      .catch(error => error.response);

    const objectDiffPlates = await data.CarsInCourtyard;

    setListPlatesPerPage([]);
    setDiffPlates(objectDiffPlates);
    setLoading(false);
  }

  async function getListPlatesPerPage() {
    setLoading(true);
    const { data } = await apiPlaca
      .get(`carSearch/${numberPerPage}/${currentPage}`)
      .then(response => response)
      .catch(error => error.response);

    const objectListPlates = await data;
    setListPlatesPerPage(objectListPlates);
    setLoading(false);
  }

  return (
    <StepContext.Provider
      value={{
        inputValue,
        listPlatesPerPage,
        currentPage,
        numberPerPage,
        operation,
        progress,
        processingPlates,
        diffPlates,
        car,
        currentStep,
        changeStep,
        handleChangeCurrentStep,
        handleReturnCurrentStep,
        handleInitialCurrentStep,
        setProgress,
        searchPlate,
        getDiffPlates,
        getListPlatesPerPage,
        setProcessingPlates,
        setOperation,
        setNumberPerPage,
        setInputValue,
        setCurrentPage,
      }}
    >
      {children}
      {loading && <Loading />}
    </StepContext.Provider>
  );
};

function useSteps(): StepContextData {
  const context = useContext(StepContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { StepProvider, useSteps };
